import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Box, IconButton, Avatar,
  Menu, MenuItem, Paper, TextField, Autocomplete, Button, Divider,
} from '@mui/material';
import { LocalHospital, ArrowBack, ExitToApp, Print } from '@mui/icons-material';
import { patientService, userService, printSettingsService, authService, issuedCertificateService } from '../../services/api';
import { generateCertNo } from '../../utils/certNumber';

const fmt = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const todayStr = () => new Date().toISOString().split('T')[0];

const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
  'Seventeen', 'Eighteen', 'Nineteen'];
const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

const numberToWords = (n) => {
  const num = parseInt(n);
  if (!n || isNaN(num) || num <= 0) return '';
  if (num < 20) return ones[num];
  if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? ' ' + ones[num % 10] : '');
  if (num < 1000) return ones[Math.floor(num / 100)] + ' Hundred' + (num % 100 ? ' ' + numberToWords(num % 100) : '');
  return String(num);
};

const FitnessCertificateForm4 = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [anchorEl, setAnchorEl] = useState(null);

  const [patients, setPatients]               = useState([]);
  const [doctors, setDoctors]                 = useState([]);
  const [printSettings, setPrintSettings]     = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedDoctor, setSelectedDoctor]   = useState(null);

  const [form, setForm] = useState(() => ({
    servantName:    '',
    practitionerOf: '',
    condition:      '',
    days:           '',
    daysInWords:    '',
    effectFrom:     todayStr(),
    date:           todayStr(),
    certNo:         generateCertNo('FCF4'),
  }));

  const printRef = useRef();

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, dRes, sRes] = await Promise.all([
          patientService.getAllPatients(),
          userService.getDoctors(),
          printSettingsService.getSettings(),
        ]);
        setPatients(pRes.data);
        setDoctors(dRes.data);
        setPrintSettings(sRes.data);
        if (user?.role === 'Doctor') {
          const match = dRes.data.find((d) => d.id === user.id);
          if (match) setSelectedDoctor(match);
        }
      } catch (err) {
        console.error('Failed to load data:', err);
      }
    };
    load();
  }, []);

  const handlePatientChange = (val) => {
    setSelectedPatient(val);
    setForm((prev) => ({
      ...prev,
      servantName: val ? `${val.firstName} ${val.lastName}` : '',
    }));
  };

  const handleField = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'days') {
        next.daysInWords = numberToWords(value);
      }
      return next;
    });
  };

  const isPrePrinted = (printSettings?.letterheadType || printSettings?.LetterheadType || 'Pre-Printed Letterhead') === 'Pre-Printed Letterhead';
  const mt = printSettings?.marginTop    ?? printSettings?.MarginTop    ?? 10;
  const mb = printSettings?.marginBottom ?? printSettings?.MarginBottom ?? 10;
  const ml = printSettings?.marginLeft   ?? printSettings?.MarginLeft   ?? 10;
  const mr = printSettings?.marginRight  ?? printSettings?.MarginRight  ?? 10;
  const hospital        = printSettings?.hospitalName || printSettings?.HospitalName || '';
  const hospitalAddress = printSettings?.address      || printSettings?.Address      || '';
  const hospitalPhone   = printSettings?.phone        || printSettings?.Phone        || '';

  const handlePrint = async () => {
    try {
      await issuedCertificateService.save({
        certificateNo:       form.certNo,
        certificateType:     'FCF4',
        certificateTypeName: 'Fitness Certificate (Form 4)',
        patientId:           selectedPatient?.id || null,
        patientName:         selectedPatient ? `${selectedPatient.firstName} ${selectedPatient.lastName}` : '',
        doctorId:            selectedDoctor?.id || null,
        doctorName:          selectedDoctor ? `Dr. ${selectedDoctor.firstName} ${selectedDoctor.lastName}` : '',
        certDate:            form.date || new Date().toISOString().split('T')[0],
        details:             JSON.stringify({ servantName: form.servantName, practitionerOf: form.practitionerOf, condition: form.condition, days: form.days, daysInWords: form.daysInWords, effectFrom: form.effectFrom }),
      });
    } catch (err) { console.error('Failed to save certificate record:', err); }

    const printContents = printRef.current.innerHTML;
    const win = window.open('', '_blank');
    win.document.write(`
      <html>
        <head>
          <title>Fitness Certificate (Form 4)</title>
          <style>
            body { font-family: Arial, sans-serif; margin: ${mt}mm ${mr}mm ${mb}mm ${ml}mm; font-size: 13px; color: #000; line-height: 2.2; }
            .f4-title { text-align: center; font-weight: bold; }
            .f4-title-sub { text-align: center; font-style: italic; font-size: 12px; line-height: 1.5; }
            .f4-right { text-align: right; margin-top: 24px; }
            .f4-right-inner { display: inline-block; text-align: center; width: 260px; }
            .f4-right-inner hr { border: none; border-top: 1px solid #000; margin: 4px 0; }
          </style>
        </head>
        <body>${printContents}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  const doctorFullName = selectedDoctor
    ? `Dr. ${selectedDoctor.firstName} ${selectedDoctor.lastName}`
    : '';
  const regNo = selectedDoctor?.medicalRegistrationNumber || '';

  // underline style helpers
  const ul    = { borderBottom: '1px solid #000', display: 'inline-block', minWidth: 220, textAlign: 'center' };
  const ulSm  = { ...ul, minWidth: 80 };
  const ulMd  = { ...ul, minWidth: 140 };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <AppBar position="static" className="medical-gradient">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/reports')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <LocalHospital sx={{ fontSize: 40, mr: 2 }} />
          <Typography variant="h5" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Fitness Certificate (Form 4)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">{user?.firstName} {user?.lastName}</Typography>
            <IconButton size="large" onClick={(e) => setAnchorEl(e.currentTarget)} color="inherit">
              <Avatar sx={{ bgcolor: '#1565c0' }}>{user?.firstName?.charAt(0)}</Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => { authService.logout(); navigate('/'); }}>
                <ExitToApp sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', gap: 2, p: 3, alignItems: 'flex-start' }}>

        {/* Left Panel */}
        <Paper elevation={3} sx={{ width: '30%', flexShrink: 0, p: 3, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>Certificate Details</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            <Autocomplete
              options={patients}
              getOptionLabel={(p) => `${p.firstName} ${p.lastName}`}
              value={selectedPatient}
              onChange={(_, val) => handlePatientChange(val)}
              renderInput={(params) => <TextField {...params} label="Select Patient (Govt. Servant)" size="small" required />}
            />

            <TextField
              label="Name of Govt. Servant (non-Gazetted)"
              size="small"
              value={form.servantName}
              onChange={handleField('servantName')}
            />

            <Autocomplete
              options={doctors}
              getOptionLabel={(d) => `Dr. ${d.firstName} ${d.lastName}`}
              value={selectedDoctor}
              onChange={(_, val) => setSelectedDoctor(val)}
              renderInput={(params) => <TextField {...params} label="Doctor (WE / I)" size="small" required />}
            />

            <TextField
              label="Registered Medical Practitioner of (Location / Hospital)"
              size="small"
              value={form.practitionerOf}
              onChange={handleField('practitionerOf')}
            />

            <TextField
              label="Suffering From (Condition)"
              size="small"
              multiline
              rows={2}
              value={form.condition}
              onChange={handleField('condition')}
            />

            <TextField
              label="Period of Absence (days — number)"
              size="small"
              type="number"
              value={form.days}
              onChange={handleField('days')}
              inputProps={{ min: 1 }}
            />

            <TextField
              label="Period of Absence (days — in words)"
              size="small"
              value={form.daysInWords}
              InputProps={{ readOnly: true }}
              sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }}
            />

            <TextField
              label="With Effect From"
              type="date"
              size="small"
              value={form.effectFrom}
              onChange={handleField('effectFrom')}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Dated, the"
              type="date"
              size="small"
              value={form.date}
              onChange={handleField('date')}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Certificate No."
              size="small"
              value={form.certNo}
              onChange={handleField('certNo')}
            />

            <Button
              variant="contained"
              startIcon={<Print />}
              onClick={handlePrint}
              sx={{ mt: 1, background: 'linear-gradient(135deg, #00897b 0%, #00695c 100%)' }}
            >
              Print Certificate
            </Button>
          </Box>
        </Paper>

        {/* Right Panel — Preview */}
        <Paper elevation={3} sx={{ flex: 1, p: 4, borderRadius: 2, minHeight: 700 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>Preview</Typography>

          <Box
            ref={printRef}
            sx={{ fontFamily: 'Arial, sans-serif', fontSize: '0.9rem', color: '#000', lineHeight: 2.2 }}
          >
            {/* Hospital header — hidden for Pre-Printed Letterhead */}
            {!isPrePrinted && (
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                  [{hospital}{hospitalAddress ? `, ${hospitalAddress}` : ''}{hospitalPhone ? `, ${hospitalPhone}` : ''}]
                </Typography>
              </Box>
            )}

            {/* Title block */}
            <Typography className="f4-title" sx={{ fontWeight: 800, fontSize: '1.05rem', textAlign: 'center', letterSpacing: 0.5 }}>
              FORM 4
            </Typography>
            <Typography className="f4-title-sub" sx={{ textAlign: 'center', fontStyle: 'italic', fontSize: '0.88rem', lineHeight: 1.5, mb: 3 }}>
              Medical Certificate for non-Gazetted Officer<br />
              recommended for leave or extension or commutation of leave
            </Typography>

            <Divider sx={{ mb: 3, borderColor: '#000' }} />

            {/* Signature line */}
            <Typography sx={{ mb: 2 }}>
              Signature of Government servant (non-Gazetted):{' '}
              <span style={ul}>{form.servantName}</span>
            </Typography>

            {/* WE / I */}
            <Typography sx={{ mb: 0 }}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;WE / I,{' '}
              <span style={{ ...ul, minWidth: 300 }}>{doctorFullName}</span>
            </Typography>

            {/* Board line */}
            <Typography sx={{ mb: 0 }}>
              members of Medical Board / Civil Surgeon &nbsp;/&nbsp; Staff Surgeon &nbsp;/&nbsp; Authorised Medical Attendant /
            </Typography>

            {/* Practitioner of */}
            <Typography sx={{ mb: 0 }}>
              Registered Medical Practitioner of{' '}
              <span style={ul}>{form.practitionerOf}</span>
              {' '}after careful examination
            </Typography>

            {/* Certify that */}
            <Typography sx={{ mb: 0 }}>
              of the case do hereby certify that{' '}
              <span style={ul}>{form.servantName}</span>
              {' '}whose signature
            </Typography>

            {/* Suffering from */}
            <Typography sx={{ mb: 0 }}>
              is given above, is suffering from{' '}
              <span style={{ ...ul, minWidth: 280 }}>{form.condition}</span>
              {' '}and
            </Typography>

            {/* Days */}
            <Typography sx={{ mb: 0 }}>
              we / I consider that a period of absence from duty of{' '}
              <span style={ulSm}>{form.days}</span>
              {' '}({' '}
              <span style={ulMd}>{form.daysInWords}</span>
              {' '}) day/s
            </Typography>

            {/* Effect from */}
            <Typography sx={{ mb: 3 }}>
              with effect from{' '}
              <span style={ulMd}>{form.effectFrom ? fmt(form.effectFrom) : ''}</span>
              {' '}is absolutely necessary for the restoration of his/her health.
            </Typography>

            {/* Dated */}
            <Typography sx={{ mb: 1 }}>
              Dated, the{' '}
              <span style={{ ...ul, minWidth: 200 }}>{form.date ? fmt(form.date) : ''}</span>
              .
            </Typography>

            {/* Certificate No */}
            <Typography sx={{ mb: 4 }}>
              Certificate No.:{' '}
              <span style={{ ...ul, minWidth: 180 }}>{form.certNo}</span>
            </Typography>

            {/* Right-aligned signature block */}
            <div className="f4-right" style={{ textAlign: 'right', marginTop: 24 }}>
              <div className="f4-right-inner" style={{ display: 'inline-block', textAlign: 'center', width: 260 }}>
                <Divider sx={{ borderColor: '#000', mb: 0.5 }} />
                <Typography sx={{ fontSize: '0.85rem', lineHeight: 1.6 }}>
                  Registered Medical Practitioner<br />
                  (Seal)
                </Typography>

                <Typography sx={{ mt: 2, textAlign: 'left', fontSize: '0.88rem' }}>
                  Regd. No.:{' '}
                  <span style={{ ...ulMd, minWidth: 100 }}>{regNo}</span>
                  .
                </Typography>
              </div>
            </div>
          </Box>
        </Paper>
      </Box>
    </div>
  );
};

export default FitnessCertificateForm4;
